import styled from '@emotion/styled';
import { Col, Row } from 'antd';
import capitalize from 'lodash/capitalize';
import React from 'react';
import { connect } from 'react-redux';

import hideIcon from 'src/public/img/hide-icon-copy.svg';
import thumbDownIconRed from 'src/public/img/icon-thumb-down.svg';
import thumbUpIconBlue from 'src/public/img/icon-thumbs-up.svg';
import thumbDownIconGrey from 'src/public/img/ic_thumb_down_24px.svg';
import thumbUpIconGrey from 'src/public/img/ic_thumb_up_24px.svg';
import showIcon from 'src/public/img/show-icon.svg';

import { dateTimeFormat } from 'src/shared/helpers/formatDateTime';
import { color, font, mediaQueries } from 'src/shared/styles/variables';

import { PlannedActivity } from '../../weekThree/models/weekThree';
import { changeAccomplishedActivityStatus } from '../actions/weekFourActions';
import { DescribeFeeling } from './DescribeFeeling';

const ActivityStatusWrapper = styled(Row)({
    padding: 0,
    marginTop: '1.75rem',
    alignItems: 'center',
});

interface ShowCompletionDescriptionIconProps {
    completed: boolean | undefined;
}

const ShowCompletionDescriptionIcon = styled('i')<ShowCompletionDescriptionIconProps>(
    ({ completed }) => ({
        display: 'block',
        height: '1.75rem',
        width: '1.75rem',
        backgroundImage: `url(${completed === true ? hideIcon : showIcon})`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
    }),
);

const ActivityTitle = styled('h5')(
    {
        color: color.chathamsBlue,
        marginBottom: 0,
    },
    font.heavy(font.size.mediumExtended),
);

const ActivityPlannedDate = styled('p')(
    {
        color: color.mamba,
        fontStyle: 'oblique',
    },
    font.roman(font.size.base),
);

const ThumbsWrapper = styled(Col)({
    display: 'flex',
    alignItems: 'center',
});

interface ThumbProps {
    completed: boolean | undefined;
    isDown?: boolean;
}

const Thumb = styled('i')<ThumbProps>(
    {
        width: '50%',
        height: '1.31rem',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        cursor: 'pointer',
        [mediaQueries.xs]: {
            height: '1.09rem',
        },
    },
    ({ isDown, completed }) =>
        isDown
            ? {
                  backgroundImage: `url(${
                      completed === false ? thumbDownIconRed : thumbDownIconGrey
                  })`,
              }
            : {
                  backgroundImage: `url(${completed ? thumbUpIconBlue : thumbUpIconGrey})`,
              },
);

interface ActionsProps {
    changeAccomplishedActivityStatus: typeof changeAccomplishedActivityStatus;
}

interface Props extends ActionsProps {
    activity: PlannedActivity;
}

class ActivityStatusContainer extends React.PureComponent<Props> {
    private handleThumbClick = (value: boolean, field: string) => () =>
        this.props.changeAccomplishedActivityStatus({
            areaTitle: this.props.activity.domainTitle,
            activityTitle: this.props.activity.title,
            value,
            field,
        });

    public render(): JSX.Element {
        const { activity } = this.props;

        return (
            <>
                <ActivityStatusWrapper type="flex">
                    <Col xs={0} sm={3} md={2} lg={1}>
                        <ShowCompletionDescriptionIcon completed={!!activity.completed} />
                    </Col>
                    <Col xs={17} sm={14} md={15} lg={16}>
                        <ActivityTitle>{capitalize(activity.title)}</ActivityTitle>
                        <ActivityPlannedDate>
                            planned for {dateTimeFormat(activity.dateToBePerformed)}
                        </ActivityPlannedDate>
                    </Col>
                    <ThumbsWrapper xs={{ span: 6, offset: 1 }}>
                        <Thumb
                            onClick={this.handleThumbClick(true, 'completed')}
                            completed={activity.completed}
                            isDown={false}
                        />
                        <Thumb
                            onClick={this.handleThumbClick(false, 'completed')}
                            completed={activity.completed}
                            isDown
                        />
                    </ThumbsWrapper>
                    {activity.completed && <DescribeFeeling activity={activity} />}
                </ActivityStatusWrapper>
            </>
        );
    }
}

export const ActivityStatus = connect(null, { changeAccomplishedActivityStatus })(
    ActivityStatusContainer,
);
